import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import esLocale from '@fullcalendar/core/locales/es';
import eventosService from '../services/eventosService';
import { clienteService } from '../services/onebaseService';
import EventoModal from './EventoModal';
import EventoDetailModal from './EventoDetailModal';
import './CalendarViewEventos.css';

import Loading from './Loading';
const CalendarViewEventos = () => {
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [selectedEventoId, setSelectedEventoId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Estados para filtros
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableClientes, setAvailableClientes] = useState([]);
  const [allClientes, setAllClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEventos();
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clienteService.getAll();
      const clientesData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      // Guardar todos los clientes para mapear nombres
      const allClientesData = clientesData.map(c => ({
        id: c.id,
        name: c.nombre || `Cliente ${c.id}`
      }));
      setAllClientes(allClientesData);
    } catch (error) {
      console.error('Error loading clientes:', error);
    }
  };

  const loadEventos = async (skipLoading = false) => {
    if (!skipLoading) {
      setLoading(true);
    }
    try {
      // Obtener rango del mes actual + un poco más
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const end = new Date();
      end.setMonth(end.getMonth() + 2);

      const startDate = start.toISOString().split('T')[0];
      const endDate = end.toISOString().split('T')[0];

      console.log('📅 Cargando eventos - Rango:', { startDate, endDate });

      const response = await eventosService.getCalendar({
        fecha_inicio: startDate,
        fecha_fin: endDate
      });

      const eventosData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      console.log('📦 Eventos recibidos:', eventosData.length);

      const formattedEvents = eventosData.map(evento => {
        const eventColor = getEventColor(evento.status);

        return {
          id: evento.id,
          title: evento.title,
          start: evento.fecha_inicio,
          end: evento.fecha_termino,
          allDay: true,
          backgroundColor: eventColor.backgroundColor,
          borderColor: evento.desarrolla_color || eventColor.backgroundColor,
          textColor: eventColor.textColor,
          extendedProps: {
            evento: evento,
            status: evento.status,
            etiqueta: evento.etiqueta || '',
            etiqueta_color: evento.etiqueta_color || '#3B82F6'
          }
        };
      });

      console.log('✅ Eventos formateados:', formattedEvents.length);
      setAllEvents(formattedEvents);
      setEvents(formattedEvents);

      // Extraer estados únicos
      const statusesUnicos = [...new Set(
        eventosData.map(evento => evento.status).filter(status => status)
      )];
      setAvailableStatuses(statusesUnicos);

      // Extraer clientes únicos que tienen eventos
      const clientesIdsConEventos = [...new Set(
        eventosData
          .filter(evento => evento.cliente)
          .map(evento => evento.cliente)
      )];

      // Mapear IDs a nombres usando allClientes
      const clientesConEventos = clientesIdsConEventos.map(clienteId => {
        const cliente = allClientes.find(c => c.id === clienteId);
        return cliente || { id: clienteId, name: `Cliente ${clienteId}` };
      });

      setAvailableClientes(clientesConEventos);

    } catch (error) {
      console.error('❌ Error loading eventos:', error);
      alert('Error al cargar los eventos');
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...allEvents];

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(event =>
        selectedStatuses.includes(event.extendedProps.status)
      );
    }

    if (selectedClientes.length > 0) {
      filtered = filtered.filter(event =>
        event.extendedProps.evento.cliente &&
        selectedClientes.includes(event.extendedProps.evento.cliente)
      );
    }

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setEvents(filtered);
  }, [selectedStatuses, selectedClientes, searchTerm, allEvents]);

  // Re-filtrar clientes cuando se cargan los eventos y ya tenemos allClientes
  useEffect(() => {
    if (allEvents.length > 0 && allClientes.length > 0) {
      const clientesIdsConEventos = [...new Set(
        allEvents
          .map(event => event.extendedProps.evento.cliente)
          .filter(clienteId => clienteId)
      )];

      const clientesConEventos = clientesIdsConEventos.map(clienteId => {
        const cliente = allClientes.find(c => c.id === clienteId);
        return cliente || { id: clienteId, name: `Cliente ${clienteId}` };
      });

      setAvailableClientes(clientesConEventos);

      // Extraer estados únicos
      const statusesUnicos = [...new Set(
        allEvents.map(event => event.extendedProps.status).filter(status => status)
      )];
      setAvailableStatuses(statusesUnicos);
    }
  }, [allEvents, allClientes]);

  const getEventColor = (status) => {
    const colors = {
      draft: { backgroundColor: '#9CA3AF', textColor: '#FFFFFF' },
      planning: { backgroundColor: '#FCD34D', textColor: '#000000' },
      confirmed: { backgroundColor: '#60A5FA', textColor: '#FFFFFF' },
      in_progress: { backgroundColor: '#A78BFA', textColor: '#FFFFFF' },
      completed: { backgroundColor: '#34D399', textColor: '#FFFFFF' },
      cancelled: { backgroundColor: '#EF4444', textColor: '#FFFFFF' },
      postponed: { backgroundColor: '#FB923C', textColor: '#FFFFFF' }
    };

    return colors[status] || { backgroundColor: '#6B7280', textColor: '#FFFFFF' };
  };

  const handleDateClick = (info) => {
    const fechaInicio = info.dateStr;
    console.log('📝 Creando nuevo evento para fecha:', fechaInicio);
    setSelectedEvento({ fecha_inicio: fechaInicio });
    setShowCreateModal(true);
  };

  const handleEventClick = (info) => {
    console.log('👆 Seleccionado evento:', info.event);
    const eventoId = info.event.id;
    setSelectedEventoId(eventoId);
    setShowDetailModal(true);
  };

  const handleCreateModalClose = () => {
    console.log('🔄 Cerrando modal de creación y recargando eventos...');
    setShowCreateModal(false);
    setSelectedEvento(null);
    loadEventos(true);
  };

  const handleDetailModalClose = () => {
    console.log('🔄 Cerrando modal de detalle...');
    setShowDetailModal(false);
    setSelectedEventoId(null);
  };

  const handleUpdate = () => {
    console.log('🔄 Recargando eventos...');
    loadEventos(true);
  };

  const handleDatesSet = (dateInfo) => {
    // Handler para cuando cambia la vista del calendario
    // Podemos usar esto para cargar más posts si es necesario
  };

  const renderEventContent = (eventInfo) => {
    const { etiqueta, etiqueta_color } = eventInfo.event.extendedProps;
    return (
      <div className="fc-event-content-wrapper">
        {etiqueta && (
          <span
            className="fc-event-etiqueta"
            style={{ backgroundColor: etiqueta_color || '#3B82F6' }}
          >
            {etiqueta}
          </span>
        )}
        <div className="fc-event-title">{eventInfo.event.title}</div>
      </div>
    );
  };

  // Filtros handlers
  const handleStatusFilterClick = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleClienteFilterClick = (clienteId) => {
    setSelectedClientes(prev => {
      if (prev.includes(clienteId)) {
        return prev.filter(id => id !== clienteId);
      } else {
        return [...prev, clienteId];
      }
    });
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSelectedClientes([]);
    setSearchTerm('');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="calendar-eventos-container">
      {/* Header */}
      <div className="calendar-eventos-header">
        <div>
          <h1 className="calendar-eventos-title">Sistema de Eventos</h1>
          <p className="calendar-eventos-subtitle">Gestiona y programa tus eventos</p>
        </div>

        <div className="calendar-eventos-header-actions">
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="🔍 Buscar por título..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                paddingLeft: '1rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                width: '250px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9ca3af';
                }}
              >
                ×
              </button>
            )}
          </div>
          <button
            onClick={() => {
              setSelectedEvento(null);
              setShowCreateModal(true);
            }}
            className="btn-primary-eventos"
          >
            + Nuevo Evento
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{ marginBottom: '1rem' }}>
        {/* Status Filters */}
        <div className="status-legend-eventos" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { status: 'draft', label: 'Borrador' },
              { status: 'planning', label: 'En Planificación' },
              { status: 'confirmed', label: 'Confirmado' },
              { status: 'in_progress', label: 'En Progreso' },
              { status: 'completed', label: 'Completado' },
              { status: 'cancelled', label: 'Cancelado' },
              { status: 'postponed', label: 'Pospuesto' },
            ]
            .filter(({ status }) => availableStatuses.includes(status))
            .map(({ status, label }) => {
              const isSelected = selectedStatuses.includes(status);
              const eventStyle = getEventColor(status);

              return (
                <button
                  key={status}
                  onClick={() => handleStatusFilterClick(status)}
                  className="filter-button-eventos"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: isSelected ? `2px solid ${eventStyle.backgroundColor}` : '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: isSelected ? eventStyle.backgroundColor : 'white',
                    color: isSelected ? eventStyle.textColor : '#374151',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? '600' : '500',
                    transition: 'all 0.2s',
                    opacity: isSelected ? 1 : 0.7
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.opacity = '1';
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.opacity = '0.7';
                  }}
                >
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: eventStyle.backgroundColor,
                      border: isSelected ? 'none' : '1px solid #d1d5db',
                      flexShrink: 0
                    }}
                  />
                  <span>{label}</span>
                  {isSelected && <span style={{ fontSize: '0.75rem' }}>✓</span>}
                </button>
              );
            })}
            {(selectedStatuses.length > 0 || selectedClientes.length > 0 || searchTerm.trim() !== '') && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '2px solid #ef4444',
                  borderRadius: '0.5rem',
                  backgroundColor: 'white',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#ef4444';
                }}
              >
                ✕ Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Cliente Filters */}
        {availableClientes.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              {availableClientes.map(cliente => {
                const isSelected = selectedClientes.includes(cliente.id);

                return (
                  <button
                    key={cliente.id}
                    onClick={() => handleClienteFilterClick(cliente.id)}
                    className="filter-button-eventos"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      border: isSelected ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      backgroundColor: isSelected ? '#f59e0b' : 'white',
                      color: isSelected ? '#ffffff' : '#374151',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? '600' : '500',
                      transition: 'all 0.2s',
                      opacity: isSelected ? 1 : 0.7
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected) e.currentTarget.style.opacity = '1';
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) e.currentTarget.style.opacity = '0.7';
                    }}
                  >
                    <span
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#f59e0b',
                        border: isSelected ? 'none' : '1px solid #d1d5db',
                        flexShrink: 0
                      }}
                    />
                    <span>{cliente.name}</span>
                    {isSelected && <span style={{ fontSize: '0.75rem' }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="loading-container-eventos">
          <Loading fullScreen={false} size="sm" mensaje="Cargando calendario de eventos" />
        </div>
      ) : (
        <div className="calendar-wrapper-eventos">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="dayGridMonth"
            locale={esLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
            }}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              list: 'Lista'
            }}
            events={events}
            eventContent={renderEventContent}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            dayMaxEvents={true}
            moreLinkText="más"
            displayEventTime={false}
            firstDay={1}
            eventDisplay="block"
            eventClassNames="fc-custom-event-eventos"
          />
        </div>
      )}

      {/* Modal de Creación */}
      {showCreateModal && (
        <EventoModal
          evento={selectedEvento}
          onClose={handleCreateModalClose}
        />
      )}

      {/* Modal de Detalle */}
      {showDetailModal && selectedEventoId && (
        <EventoDetailModal
          eventoId={selectedEventoId}
          onClose={handleDetailModalClose}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
};

export default CalendarViewEventos;
